import { useEffect, useState } from "react";
import { CheckCircle2, Circle, PlayCircle, Route, ChevronDown, ChevronUp, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Skeleton } from "@/components/Skeleton";

/**
 * Six-Stage Rehabilitation Roadmap (S1–S6) · Phase 3 · Part A.
 *
 * A SEPARATE, clinician-controlled stage track that lives alongside the
 * existing clinical-phase roadmap. The clinician assigns a status + rationale
 * to each stage (only one may be "In progress"); the patient sees it read-only
 * and only once the clinician confirms it.
 */

const STATUS_META = {
  not_started: { label: "Not started", color: "text-zinc-500", border: "border-white/10", Icon: Circle },
  in_progress: { label: "In progress", color: "text-[#f47a20]", border: "border-[#f47a20]/60", Icon: PlayCircle },
  completed:   { label: "Completed",   color: "text-emerald-400", border: "border-emerald-500/40", Icon: CheckCircle2 },
};

function StageCard({ stage, idx, total, testidPrefix }) {
  const meta = STATUS_META[stage.status] || STATUS_META.not_started;
  const Icon = meta.Icon;
  return (
    <li className={`fl-card p-3 border ${meta.border}`} data-testid={`${testidPrefix}-stage-${idx}`}>
      <div className={`flex items-center gap-2 ${meta.color}`}>
        <Icon size={14} />
        <span className="text-[10px] uppercase tracking-widest">{meta.label}</span>
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-[11px] font-bold text-[#f47a20] shrink-0">{stage.key}</span>
        <span className="fl-heading text-sm break-words">{stage.title}</span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-1">Stage {idx + 1} of {total}</div>
      {stage.rationale && (
        <p className="text-xs text-zinc-400 mt-2 leading-relaxed" data-testid={`${testidPrefix}-rationale-${idx}`}>
          {stage.rationale}
        </p>
      )}
    </li>
  );
}

/* ====================== Patient read-only view ====================== */
export function PatientRehabStages({ fallback = null }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/rehab-stages/mine")
      .then((r) => { if (alive) { setData(r.data); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  if (!loaded) return fallback;
  if (!data?.confirmed) return fallback;

  const stages = data.stages || [];
  return (
    <div className="fl-card fl-card-elevated p-4 sm:p-5 mb-4" data-testid="patient-rehab-stages">
      <div className="flex items-center gap-2">
        <Route size={14} className="text-[#f47a20]" />
        <div className="fl-label text-xs">Your rehabilitation roadmap</div>
      </div>
      <ol className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="patient-rehab-stages-timeline">
        {stages.map((s, i) => (
          <StageCard key={s.key} stage={s} idx={i} total={stages.length} testidPrefix="patient-rehab" />
        ))}
      </ol>
      <p className="text-[11px] text-zinc-500 mt-3">
        Your clinician reviews and confirms each stage. The stage shows where you are in your
        rehabilitation, not a recovery percentage.
      </p>
    </div>
  );
}

/* ====================== Clinician management ====================== */
export function RehabStageManager({ patientId }) {
  const [open, setOpen] = useState(false);
  const [stages, setStages] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rehab-stages/patient/${patientId}`);
      setStages(data.stages || []);
      setConfirmed(!!data.confirmed);
      setLoaded(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load rehabilitation stages");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && !loaded) load(); /* eslint-disable-next-line */ }, [open]);

  const setStatus = (key, status) => {
    setStages((ss) => ss.map((s) => {
      if (s.key === key) return { ...s, status };
      // Enforce a single "In progress" stage.
      if (status === "in_progress" && s.status === "in_progress") return { ...s, status: "not_started" };
      return s;
    }));
  };
  const setRationale = (key, rationale) =>
    setStages((ss) => ss.map((s) => (s.key === key ? { ...s, rationale } : s)));

  const save = async () => {
    const inProg = stages.filter((s) => s.status === "in_progress");
    if (inProg.length > 1) return toast.error("Only one stage can be In progress.");
    if (inProg.length === 1 && !(inProg[0].rationale || "").trim())
      return toast.error("Add a rationale for the In progress stage.");
    setSaving(true);
    try {
      const { data } = await api.put(`/rehab-stages/patient/${patientId}`, {
        stages: stages.map(({ key, status, rationale }) => ({ key, status, rationale: rationale || "" })),
        confirm: true,
      });
      setStages(data.stages || []);
      setConfirmed(!!data.confirmed);
      toast.success("Rehabilitation stages saved and shared with the patient");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4 border-t border-white/5 pt-3" data-testid="rehab-stage-manager">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full gap-2 text-left"
        data-testid="rehab-stage-toggle"
      >
        <span className="flex items-center gap-2">
          <Route size={14} className="text-[#f47a20]" />
          <span className="fl-heading text-sm">Rehabilitation stage management (S1–S6)</span>
          {confirmed && <span className="text-[10px] text-emerald-400 uppercase tracking-widest">· visible to patient</span>}
        </span>
        {open ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
      </button>

      {open && (
        <div className="mt-3">
          {loading && <Skeleton className="h-24 rounded" data-testid="rehab-stage-loading" />}
          {!loading && (
            <>
              <p className="text-[11px] text-zinc-500 mb-3">
                Assign a status and rationale to each stage. Only one stage can be "In progress".
                Saving confirms the stages and makes them visible to the patient. S6 REVIEW does not discharge the patient.
              </p>
              <div className="space-y-2">
                {stages.map((s) => (
                  <div key={s.key} className="fl-card p-3 border border-white/10" data-testid={`rehab-stage-row-${s.key}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-[#f47a20] w-7">{s.key}</span>
                      <span className="fl-heading text-sm flex-1 min-w-0 truncate">{s.title}</span>
                      <select
                        className="fl-input text-xs"
                        value={s.status}
                        onChange={(e) => setStatus(s.key, e.target.value)}
                        data-testid={`rehab-stage-status-${s.key}`}
                      >
                        <option value="not_started">Not started</option>
                        <option value="in_progress">In progress</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <input
                      className="fl-input text-sm mt-2 w-full"
                      placeholder="Rationale (shared with the patient)"
                      value={s.rationale || ""}
                      onChange={(e) => setRationale(s.key, e.target.value)}
                      data-testid={`rehab-stage-rationale-${s.key}`}
                    />
                  </div>
                ))}
              </div>
              <button className="fl-btn text-xs mt-3" onClick={save} disabled={saving} data-testid="rehab-stage-save">
                <Save size={12} /> {saving ? "Saving…" : "Save & confirm for patient"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
